export interface YearlyProjection {
  year: number;
  projectedGenerationMwh: number;
  avoidedCo2eKg: number;
  avoidedDalys: number;
  avoidedPdfs: number;
  economicValueNominalUsd: number;
  economicValueNpvUsd: number;
}

export interface LifetimeProjectionConfig {
  projectLifetimeYears: number;
  socialDiscountRate: number;
  annualDegradationPct: number;
  degradationModel: string;
  includeConstructionImpacts: boolean;
  includeDecommissioningImpacts: boolean;
}

export interface LifetimeProjection {
  projectId: string;
  config: LifetimeProjectionConfig;
  codYear: number;
  baseYearGenerationMwh: number | null | undefined;
  baseYearCapacityFactor: number;
  impactRates: {
    dalysPerMwh: number;
    pdfsPerMwh: number;
    usdPerMwh: number;
    co2eKgPerMwh: number;
  };
  yearlyProjections: YearlyProjection[];
  lifetimeGenerationGwh: number;
  lifetimeAvoidedCo2eTonnes: number;
  lifetimeAvoidedDalys: number;
  lifetimeAvoidedPdfs: number;
  lifetimeEconomicNominalUsd: number;
  lifetimeEconomicNpvUsd: number;
  infrastructureCo2eTonnes: number | null;
  netLifetimeCo2eTonnes: number;
  calculatedAt: string;
  modelVersion: string;
}

/**
 * The default project lifetime -- mirrors DEFAULT_PROJECT_LIFETIME_YEARS in
 * packages/schemas/src/totalview_schemas/domain/lifetime.py, the one resolver the
 * engine and the API use. 25 years for EVERY technology. (This file used to give
 * onshore wind 20 while the API projected 25; "offshore 30" was never implemented
 * anywhere -- see the Python resolver's docstring.)
 */
export const DEFAULT_PROJECT_LIFETIME_YEARS = 25;

/**
 * The lifetime to chart: the project's stored project_lifetime_years, else the
 * lifetime the run stamped (summary_json.execution_metadata.project_lifetime.years),
 * else the default.
 */
export function resolveProjectLifetimeYears(
  projectValue: number | "" | null | undefined,
  runStampedYears?: number | null,
): number {
  if (typeof projectValue === "number" && projectValue > 0) return projectValue;
  if (typeof runStampedYears === "number" && runStampedYears > 0) return runStampedYears;
  return DEFAULT_PROJECT_LIFETIME_YEARS;
}

/** Technology-specific degradation. The lifetime is NOT here: see resolveProjectLifetimeYears. */
export const DEFAULT_LIFETIME_CONFIG: Record<string, {
  annualDegradationPct: number;
  degradationModel: string;
}> = {
  solar_ground: {
    annualDegradationPct: 0.005,
    degradationModel: "exponential",
  },
  solar_rooftop: {
    annualDegradationPct: 0.005,
    degradationModel: "exponential",
  },
  wind_onshore: {
    annualDegradationPct: 0.016,
    degradationModel: "exponential",
  },
};

export function generateLifetimeProjection(
  baseYearMwh: number,
  impactRates: Record<string, number>,
  config: LifetimeProjectionConfig,
  codYear: number
): YearlyProjection[] {
  const projections: YearlyProjection[] = [];

  for (let i = 0; i < config.projectLifetimeYears; i++) {
    // Apply degradation
    const degradationFactor =
      config.degradationModel === "exponential"
        ? Math.pow(1 - config.annualDegradationPct, i)
        : Math.max(0, 1 - config.annualDegradationPct * i);

    const projectedMwh = baseYearMwh * degradationFactor;

    // Calculate impacts using per-MWh rates
    const avoidedCo2eKg = projectedMwh * (impactRates.co2eKgPerMwh || 0);
    const avoidedDalys = projectedMwh * (impactRates.dalysPerMwh || 0);
    const avoidedPdfs = projectedMwh * (impactRates.pdfsPerMwh || 0);
    const economicNominal = projectedMwh * (impactRates.usdPerMwh || 0);

    // NPV discount factor: 1 / (1 + r)^i
    const discountFactor = Math.pow(1 + config.socialDiscountRate, -i);
    const economicNpv = economicNominal * discountFactor;

    projections.push({
      year: codYear + i,
      projectedGenerationMwh: projectedMwh,
      avoidedCo2eKg,
      avoidedDalys,
      avoidedPdfs,
      economicValueNominalUsd: economicNominal,
      economicValueNpvUsd: economicNpv,
    });
  }

  return projections;
}
